import { API_CONFIG } from "../config/api";
import { authenticatedApi } from "../utils/api";

export const FITBIT_COLLECTION_PARTICIPANT_INVITATIONS_PATH =
  "/api/fitbit-collection/participant-invitations";
export const FITBIT_COLLECTION_AUTHORIZATION_START_PATH =
  "/api/fitbit-collection/authorization/start";
export const FITBIT_COLLECTION_PROFILE_RESUME_START_PATH =
  "/api/fitbit-collection/profile-resume/start";
export const FITBIT_COLLECTION_PROFILE_COMPLETION_PATH =
  "/api/fitbit-collection/enrollment/profile";

export type FitbitCollectionProfile = {
  firstName: string;
  lastName: string;
  age: number;
  gender: string;
  phoneNumber: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  additionalInfo: string | null;
};

export type FitbitCollectionProfileCompletion = {
  result: "fitbit_collection_profile_completion_complete";
  historicalCollectionQueued: boolean;
};

export type FitbitCollectionInvitationIssue = {
  result: "fitbit_collection_invitation_issued";
  expiresAt: string;
};

function apiUrl(path: string): string {
  return `${API_CONFIG.BASE_URL.replace(/\/+$/, "")}${path}`;
}

export function fitbitCollectionAuthorizationStartUrl(): string {
  return apiUrl(FITBIT_COLLECTION_AUTHORIZATION_START_PATH);
}

export function fitbitCollectionProfileResumeStartUrl(): string {
  return apiUrl(FITBIT_COLLECTION_PROFILE_RESUME_START_PATH);
}

function isProfileCompletion(value: unknown): value is FitbitCollectionProfileCompletion {
  return Boolean(value) && typeof value === "object" &&
    (value as FitbitCollectionProfileCompletion).result ===
      "fitbit_collection_profile_completion_complete" &&
    typeof (value as FitbitCollectionProfileCompletion).historicalCollectionQueued === "boolean";
}

function isInvitationIssue(value: unknown): value is FitbitCollectionInvitationIssue {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const result = value as Record<string, unknown>;
  return Object.keys(result).length === 2 &&
    result.result === "fitbit_collection_invitation_issued" &&
    typeof result.expiresAt === "string" &&
    Number.isFinite(Date.parse(result.expiresAt));
}

/**
 * Ask the authenticated fresh-collection runtime to create and deliver one
 * opaque invitation. The invitation token never crosses this client boundary.
 */
export async function issueFitbitCollectionParticipantInvitation(
  studyId: string,
  email: string,
  phoneNumber: string | null,
  token: string,
  message?: { subject: string; body: string },
): Promise<FitbitCollectionInvitationIssue> {
  const result: unknown = await authenticatedApi.postOnce(
    FITBIT_COLLECTION_PARTICIPANT_INVITATIONS_PATH,
    { studyId, email, phoneNumber, ...(message ? { message } : {}) },
    token,
  );

  if (!isInvitationIssue(result)) {
    throw new Error("We could not confirm the Fitbit invitation. Please try again.");
  }

  return result;
}

/**
 * This is deliberately separate from the legacy Fitbit and Google Health
 * services. The opaque, HttpOnly profile-completion cookie is the only
 * identity proof accepted by the fresh collection endpoint.
 */
export async function completeFitbitCollectionProfile(
  profile: FitbitCollectionProfile,
  profileSession?: string,
): Promise<FitbitCollectionProfileCompletion> {
  if (profileSession !== undefined && !/^[A-Za-z0-9_-]{43}$/.test(profileSession)) {
    throw new Error("This enrollment session is invalid. Reopen your invitation.");
  }
  const response = await fetch(apiUrl(FITBIT_COLLECTION_PROFILE_COMPLETION_PATH), {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ profile, ...(profileSession === undefined ? {} : { profileSession }) }),
  });

  if (!response.ok) {
    throw new Error("We could not complete your enrollment. Please try again.");
  }

  const result: unknown = await response.json();
  if (!isProfileCompletion(result)) {
    throw new Error("We could not confirm your enrollment. Please try again.");
  }

  return result;
}
